import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ChevronDown, ChevronRight } from "lucide-react";
import { SERVICES, SPA_SERVICES, IMAGES } from "../data/site";
import { openWeaveBookingInNewTab, WEAVE_BOOKING_URL } from "../lib/weaveBooking";

export const getBannerObjectPosition = (bannerFocus) => {
  if (bannerFocus === "top" || bannerFocus === "bottom") {
    return `center ${bannerFocus}`;
  }

  const match = bannerFocus?.match(/^(top|bottom)-(\d{1,3})$/);
  if (!match) return "center center";

  const [, edge, offset] = match;
  const percentage = Math.min(Number(offset), 100);
  return `center ${edge === "top" ? percentage : 100 - percentage}%`;
};

export const ServiceDetailPage = () => {
  const { slug, subslug } = useParams();
  const parentService = SERVICES.find((s) => s.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);

  if (!parentService) return <Navigate to="/services" replace />;

  // If a subslug is present, find the highlight and use it as the rendered service
  const subService = subslug
    ? parentService.highlights?.find((h) => h.slug === subslug)
    : null;
  if (subslug && !subService) return <Navigate to={`/services/${slug}`} replace />;

  const service = subService ?? parentService;

  return (
    <div data-testid="service-detail-page">
      {/* Hero */}
      <section className="relative h-[60vh]  overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: getBannerObjectPosition(service.bannerfocus) }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/40 to-ink/80" />
        <div className="relative h-full container-x flex flex-col justify-end pb-20 text-white">
          {/* <nav className="text-[11px] tracking-[0.3em] uppercase text-white/60 mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight size={12} />
            <Link to="/services" className="hover:text-gold">Services</Link>
            <ChevronRight size={12} />
            {subService && (
              <>
                <Link to={`/services/${parentService.slug}`} className="hover:text-gold">{parentService.name}</Link>
                <ChevronRight size={12} />
              </>
            )}
            <span className="text-gold-light">{service.name}</span>
          </nav> */}
          {(service.category ?? parentService.category) && (
            <div className="label-eyebrow text-gold text-xl mb-6">{service.category ?? parentService.category}</div>
          )}
          <h1 className="heading-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight text-white max-w-4xl">{service.name}</h1>
          {/* {service.tagline && (
            <p className="font-serif italic text-xl md:text-2xl text-white/80 mt-6 max-w-2xl">{service.tagline}</p>
          )} */}
        </div>
      </section>

      {/* Summary */}
      {(Array.isArray(service.summary) ? service.summary.some(Boolean) : service.summary) && (
      <section className="py-10 container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-12  text-lg leading-[1.8] text-ink/80">
            {Array.isArray(service.summary)
              ? service.summary.filter(Boolean).map((para, i) => <p key={i} className={`whitespace-pre-line${i > 0 ? " mt-6" : ""}`}>{para}</p>)
              : <p className="whitespace-pre-line">{service.summary}</p>}
          </div>
        </div>
      </section>
      )}

      {/* Service Highlights */}
      {service.highlights?.length > 0 && (
      <section className="py-10 md:py-3 container-x" data-testid="service-highlights-section">
        {/* <SectionHeading eyebrow="Included" title="What's" italicWord="involved." /> */}
        <div className={`mt-16 grid grid-cols-1 gap-px bg-ink/10 ${
          service.highlights.length === 1 ? "md:grid-cols-1" : 
          service.highlights.length === 2 ? "md:grid-cols-2" :
          service.highlights.length === 4 ? "md:grid-cols-2" : 
          "md:grid-cols-3"
        }`}>
          {service.highlights.map((s, i) => (
            <div
              key={s.slug}
              data-testid={`service-highlight-${s.slug}`}
              className="group bg-cream p-8 md:p-10 reveal flex flex-col"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {s.image && (
                <div className="aspect-[4/3] overflow-hidden mb-6">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110" />
                </div>
              )}
              <div className="label-eyebrow mb-3">0{i + 1}</div>
              <h3 className="font-serif text-2xl font-light leading-tight">{s.name}</h3>
              {s.tagline && <p className="font-serif italic text-gold-dark mt-2 text-sm">{s.tagline}</p>}
              {/* {s.summary && <p className="text-ink/60 text-sm mt-4 leading-relaxed flex-1">{s.summary}</p>} */}
              
              {/* <ul className="mt-6 flex flex-col gap-1.5">
                {s.benefits?.length > 0 && s.benefits.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-[11px] tracking-wide text-ink/70">
                    <span className="w-1 h-1 rounded-full bg-gold flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul> */}
              <Link
                to={`/services/${service.slug}/${s.slug}`}
                className="mt-6 text-[11px] tracking-[0.2em] uppercase text-gold hover:text-ink transition-colors flex items-center gap-1.5"
              >
                Read More <ChevronRight size={12} />
              </Link>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Benefits */}
      {service.benefits?.length > 0 && (
      <section className="py-24 md:py-32 bg-ink text-bone">
        <div className="container-x">
          <h2 className="font-serif text-4xl md:text-5xl font-light mb-2">Why patients <span className="italic">choose us.</span></h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {service.benefits.map((b, i) => (
              <div key={i} className="bg-ink p-10 flex items-start gap-5 reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <Check size={20} strokeWidth={1.2} className="text-gold mt-1 flex-shrink-0" />
                <div className="font-serif text-2xl font-light leading-tight">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Process */}
      {service.process?.length > 0 && (
      <section className="py-24 md:py-32 container-x">
        <h2 className="font-serif text-4xl md:text-5xl font-light mb-2">How it <span className="italic">unfolds.</span></h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          {service.process.map((p, i) => (
            <div key={i} className="reveal" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`process-step-${i}`}>
              <div className="font-serif text-5xl md:text-6xl font-light text-gold mb-6">{String(i + 1).padStart(2, "0")}</div>
              {p.step && <h3 className="font-serif text-2xl font-medium mb-3">{p.step}</h3>}
              {p.body && <p className="text-ink/60 leading-relaxed">{p.body}</p>}
            </div>
          ))}
        </div>
      </section>
      )}


      {/* FAQ */}
      {service.faqs?.length > 0 && (
      <section className="py-24 md:py-32 container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <h2 className="font-serif text-4xl md:text-5xl font-light mb-2">Questions, <span className="italic">answered.</span></h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <div className="divide-y divide-ink/15">
              {service.faqs.map((f, i) => (
                <div key={i} data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full py-6 flex items-center justify-between text-left group"
                  >
                    <span className="font-serif text-xl md:text-2xl font-light pr-8 group-hover:text-gold transition-colors">{f.q}</span>
                    <ChevronDown size={20} strokeWidth={1.2} className={`flex-shrink-0 transition-transform duration-500 ${openFaq === i ? "rotate-180 text-gold" : ""}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-500 ${openFaq === i ? "max-h-64 pb-8" : "max-h-0"}`}>
                    <p className="text-ink/70 leading-[1.8] pr-12">{f.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* CTA Banner */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
        </div>
        <div className="relative container-x text-center text-white">
          <h2 className="heading-display text-4xl md:text-6xl">
            Ready for{service.name && <span className="italic text-gold-light"> {service.name.toLowerCase()}?</span>}
          </h2>
          <a href={WEAVE_BOOKING_URL} onClick={openWeaveBookingInNewTab} data-testid="service-detail-cta" className="btn-outline-light mt-10 inline-flex">Book a Consultation</a>
        </div>
      </section>
    </div>
  );
};

// Default export for handling dental and spa services with kind prop
const ServiceDetail = ({ kind }) => {
  const { slug } = useParams();
  
  const list = kind === "dental" ? SERVICES : SPA_SERVICES;
  const service = list?.find((s) => s.slug === slug);
  
  if (!service) return <Navigate to={kind === "dental" ? "/dental-services" : "/spa-services"} replace />;

  return (
    <div>
      <section className="relative bg-[#FBF8F1] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <p className="font-script text-3xl md:text-4xl gold-gradient-text leading-none mb-1">Discover</p>
            <h2 className="font-cinzel text-2xl md:text-3xl gold-gradient-text uppercase tracking-[0.15em]">
              {service.name}
            </h2>
            <div className="w-16 h-px bg-[#B79A68] my-6" />
            <p className="text-[#4E5750] text-lg leading-relaxed mb-5">{service.tagline}</p>
            {service.summary && (typeof service.summary === "string" ? (
              <p className="text-[#4E5750]/85 leading-relaxed mb-4">{service.summary}</p>
            ) : (
              service.summary.map((p, i) => (
                <p key={i} className="text-[#4E5750]/85 leading-relaxed mb-4">{p}</p>
              ))
            ))}
            <div className="mt-8">
              <h3 className="font-cinzel text-sm gold-gradient-text uppercase tracking-[0.2em] mb-4">Key Benefits</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {service.benefits?.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[#4E5750]/85">
                    <span className="shrink-0 w-5 h-5 rounded-full border border-[#B79A68] flex items-center justify-center mt-0.5">
                      <Check size={11} className="text-[#7D6A42]" />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <img src={service.image} alt={service.name} className="w-full h-72 object-cover border border-[#B79A68]/40" />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
